import { shouldSkipObject } from '@/threejs/objectManager';
import * as THREE from 'three';
import type { Projector } from './projector';

type OverlapUserData = {
  projectorOverlapIgnore?: boolean;
  projectorOverlayIgnore?: boolean;
  projectorOverlapState?: {
    transparent: boolean;
    depthWrite: boolean;
    depthTest: boolean;
    blending: THREE.Blending;
    premultipliedAlpha: boolean;
  };
  projectorFrustumOverlap?: {
    applied?: boolean;
    uniforms?: THREE.Shader['uniforms'];
  };
};

export type ProjectorFrustumOverlapMode = 'none' | 'mix' | 'only';

export type ProjectorFrustumOverlapOptions = {
  maxProjectors?: number;
  color?: THREE.ColorRepresentation;
  fillColor?: THREE.ColorRepresentation;
  fillOpacity?: number;
  overlapColor?: THREE.ColorRepresentation;
  overlapOpacity?: number;
  overlapMode?: ProjectorFrustumOverlapMode;
  depthSize?: number;
  depthBias?: number;
  facingThreshold?: number;
  useTextureArray?: boolean;
};

export class ProjectorFrustumOverlap {
  private readonly maxProjectors: number;
  private readonly color: THREE.Color;
  private readonly fillColor: THREE.Color;
  private readonly overlapColor: THREE.Color;
  private fillOpacity: number;
  private overlapOpacity: number;
  private overlapMode: ProjectorFrustumOverlapMode;
  private overlapModeValue: number;
  private readonly materials = new Set<THREE.Material>();
  private readonly matrices: THREE.Matrix4[];
  private readonly projectorPositions: THREE.Vector3[];
  private depthTargets: THREE.WebGLRenderTarget[] = [];
  private depthTextures: THREE.DepthTexture[] = [];
  private readonly depthArrayTarget?: THREE.WebGLArrayRenderTarget;
  private readonly depthMaterial: THREE.MeshDepthMaterial;
  private readonly depthSize: number;
  private readonly depthBias: number;
  private readonly useTextureArray: boolean;
  private readonly overrideMaterial: THREE.MeshStandardMaterial;
  private facingThreshold: number;
  private overlapOnly = false;

  constructor(options: ProjectorFrustumOverlapOptions = {}) {
    this.maxProjectors = Math.max(1, options.maxProjectors ?? 16);
    this.color = new THREE.Color(options.color ?? 0x77e4ff);
    this.fillColor = new THREE.Color(options.fillColor ?? 0x2aa6ff);
    this.overlapColor = new THREE.Color(options.overlapColor ?? 0xff5a6a);
    this.fillOpacity = options.fillOpacity ?? 0.12;
    this.overlapOpacity = options.overlapOpacity ?? 0.22;
    this.overlapMode = options.overlapMode ?? 'mix';
    this.overlapModeValue = this.getOverlapModeValue(this.overlapMode);
    this.depthSize = options.depthSize ?? 1024;
    this.depthBias = options.depthBias ?? 0.0025;
    this.facingThreshold = options.facingThreshold ?? -1;
    this.useTextureArray = options.useTextureArray ?? false;
    this.matrices = Array.from({ length: this.maxProjectors }, () => new THREE.Matrix4());
    this.projectorPositions = Array.from({ length: this.maxProjectors }, () => new THREE.Vector3());
    this.depthMaterial = new THREE.MeshDepthMaterial();
    this.depthMaterial.blending = THREE.NoBlending;

    if (this.useTextureArray) {
      this.depthMaterial.depthPacking = THREE.RGBADepthPacking;
      const target = new THREE.WebGLArrayRenderTarget(this.depthSize, this.depthSize, this.maxProjectors, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        depthBuffer: true,
        stencilBuffer: false,
      });
      target.texture.minFilter = THREE.NearestFilter;
      target.texture.magFilter = THREE.NearestFilter;
      target.texture.generateMipmaps = false;
      target.texture.wrapS = THREE.ClampToEdgeWrapping;
      target.texture.wrapT = THREE.ClampToEdgeWrapping;
      this.depthArrayTarget = target;
    } else {
      this.depthTargets = Array.from({ length: this.maxProjectors }, () => {
        const target = new THREE.WebGLRenderTarget(this.depthSize, this.depthSize);
        target.texture.minFilter = THREE.NearestFilter;
        target.texture.magFilter = THREE.NearestFilter;
        target.texture.generateMipmaps = false;
        target.depthTexture = new THREE.DepthTexture(this.depthSize, this.depthSize);
        target.depthTexture.format = THREE.DepthFormat;
        target.depthTexture.type = THREE.UnsignedIntType;
        target.depthTexture.minFilter = THREE.NearestFilter;
        target.depthTexture.magFilter = THREE.NearestFilter;
        return target;
      });
      this.depthTextures = this.depthTargets.map((target) => target.depthTexture);
    }

    this.overrideMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.6,
      metalness: 0.0,
    });
    this.decorateMaterial(this.overrideMaterial);
  }

  applyTo(root: THREE.Object3D) {
    root.traverse((object) => {
      if (!('isMesh' in object) || !(object as THREE.Mesh).isMesh) {
        return;
      }

      const mesh = object as THREE.Mesh;
      if ((mesh.userData as OverlapUserData).projectorOverlapIgnore) {
        return;
      }

      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((material) => this.decorateMaterial(material));
      } else if (mesh.material) {
        this.decorateMaterial(mesh.material);
      }
    });
  }

  update(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    projectors: Projector[],
    options: { overlapOnly?: boolean } = {}
  ) {
    const count = Math.min(projectors.length, this.maxProjectors);
    this.overlapOnly = options.overlapOnly ?? false;
    if (count === 0) {
      this.applyUniforms(0);
      return;
    }

    const previousRenderTarget = renderer.getRenderTarget();
    const previousClearColor = new THREE.Color();
    renderer.getClearColor(previousClearColor);
    const previousClearAlpha = renderer.getClearAlpha();
    const previousAutoClear = renderer.autoClear;
    const previousOverrideMaterial = scene.overrideMaterial;

    renderer.autoClear = true;
    scene.overrideMaterial = this.depthMaterial;
    renderer.setClearColor(0x000000, 1);

    const hiddenObjects: THREE.Object3D[] = [];
    scene.traverse((object: THREE.Object3D) => {
      if (object.visible && shouldSkipObject(object)) {
        object.visible = false;
        hiddenObjects.push(object);
      }
    });

    for (let i = 0; i < count; i += 1) {
      const projector = projectors[i];
      projector.updateMatrixWorld(true);
      projector.camera.updateMatrixWorld(true);
      this.matrices[i].multiplyMatrices(projector.camera.projectionMatrix, projector.camera.matrixWorldInverse);
      this.projectorPositions[i].setFromMatrixPosition(projector.camera.matrixWorld);

      if (this.useTextureArray) {
        if (!this.depthArrayTarget) {
          continue;
        }
        renderer.setRenderTarget(this.depthArrayTarget, i);
      } else {
        renderer.setRenderTarget(this.depthTargets[i]);
      }
      renderer.clear();
      renderer.render(scene, projector.camera);
    }

    for (const object of hiddenObjects) {
      object.visible = true;
    }

    renderer.setRenderTarget(previousRenderTarget);
    renderer.setClearColor(previousClearColor, previousClearAlpha);
    renderer.autoClear = previousAutoClear;
    scene.overrideMaterial = previousOverrideMaterial;

    this.applyUniforms(count);
  }

  setFillOpacity(opacity: number) {
    this.fillOpacity = Math.min(1, Math.max(0, opacity));
  }

  setOverlapOpacity(opacity: number) {
    this.overlapOpacity = Math.min(1, Math.max(0, opacity));
  }

  setFacingThreshold(threshold: number) {
    this.facingThreshold = Math.max(-1, Math.min(1, threshold));
  }

  setOverlapMode(mode: ProjectorFrustumOverlapMode) {
    this.overlapMode = mode;
    this.overlapModeValue = this.getOverlapModeValue(mode);
  }

  getOverlapMode() {
    return this.overlapMode;
  }

  clearOverlap() {
    this.overlapOnly = false;
    this.applyUniforms(0);
  }

  getBatchSize() {
    return this.maxProjectors;
  }

  getOverrideMaterial() {
    return this.overrideMaterial;
  }

  beginOverlapPass() {
    for (const material of this.materials) {
      const userData = material.userData as OverlapUserData;
      if (!userData.projectorOverlapState) {
        userData.projectorOverlapState = {
          transparent: material.transparent,
          depthWrite: material.depthWrite,
          depthTest: material.depthTest,
          blending: material.blending,
          premultipliedAlpha: material.premultipliedAlpha,
        };
      }

      // Use additive blending for proper accumulation across multiple render passes
      material.transparent = true;
      material.depthWrite = false;
      material.depthTest = true;
      material.blending = THREE.AdditiveBlending;
      material.premultipliedAlpha = false;
    }
  }

  endOverlapPass() {
    for (const material of this.materials) {
      const userData = material.userData as OverlapUserData;
      const state = userData.projectorOverlapState;
      if (!state) {
        continue;
      }

      material.transparent = state.transparent;
      material.depthWrite = state.depthWrite;
      material.depthTest = state.depthTest;
      material.blending = state.blending;
      material.premultipliedAlpha = state.premultipliedAlpha;
    }
  }

  private decorateMaterial(material: THREE.Material) {
    if (!('isMeshStandardMaterial' in material) && !('isMeshPhysicalMaterial' in material)) {
      return;
    }

    const userData = material.userData as OverlapUserData;
    if (userData.projectorFrustumOverlap?.applied) {
      return;
    }

    const previous = material.onBeforeCompile;
    material.onBeforeCompile = (shader) => {
      previous?.(shader);

      shader.uniforms.uProjectorOverLapCount = { value: 0 };
      shader.uniforms.uProjectorOverLapMatrix = {
        value: this.matrices.map((m) => m.clone()),
      };
      shader.uniforms.uProjectorOverLapPosition = {
        value: this.projectorPositions.map((p) => p.clone()),
      };
      shader.uniforms.uProjectorOverLapColor = { value: this.color };
      shader.uniforms.uProjectorOverLapFillColor = { value: this.fillColor };
      shader.uniforms.uProjectorOverLapFillOpacity = {
        value: this.fillOpacity,
      };
      shader.uniforms.uProjectorOverLapOverlapColor = {
        value: this.overlapColor,
      };
      shader.uniforms.uProjectorOverLapOverlapOpacity = {
        value: this.overlapOpacity,
      };
      shader.uniforms.uProjectorOverLapOverlapMode = {
        value: this.overlapModeValue,
      };
      shader.uniforms.uProjectorOverLapOverlapOnly = { value: 0 };
      shader.uniforms.uProjectorOverLapBias = { value: this.depthBias };
      shader.uniforms.uProjectorOverLapFacingThreshold = {
        value: this.facingThreshold,
      };
      if (this.useTextureArray) {
        shader.uniforms.uProjectorOverLapDepthArray = {
          value: this.depthArrayTarget?.texture ?? null,
        };
      } else {
        for (let i = 0; i < this.depthTextures.length; i += 1) {
          shader.uniforms[`uProjectorOverLapDepth${i}`] = {
            value: this.depthTextures[i],
          };
        }
      }

      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\n' + 'varying vec3 vProjectorOverLapWorldPosition;')
        .replace(
          '#include <project_vertex>',
          [
            '#include <project_vertex>',
            'vec4 projectorOverlapWorldPosition = vec4( transformed, 1.0 );',
            '#ifdef USE_BATCHING',
            '  projectorOverlapWorldPosition = batchingMatrix * projectorOverlapWorldPosition;',
            '#endif',
            '#ifdef USE_INSTANCING',
            '  projectorOverlapWorldPosition = instanceMatrix * projectorOverlapWorldPosition;',
            '#endif',
            'projectorOverlapWorldPosition = modelMatrix * projectorOverlapWorldPosition;',
            'vProjectorOverLapWorldPosition = projectorOverlapWorldPosition.xyz;',
          ].join('\n')
        );

      const fragmentHeaderLines = [
        '#include <common>',
        ...(this.useTextureArray ? ['#include <packing>'] : []),
        `#define MAX_PROJECTORS ${this.maxProjectors}`,
        'varying vec3 vProjectorOverLapWorldPosition;',
        'uniform int uProjectorOverLapCount;',
        'uniform mat4 uProjectorOverLapMatrix[MAX_PROJECTORS];',
        'uniform vec3 uProjectorOverLapPosition[MAX_PROJECTORS];',
        'uniform vec3 uProjectorOverLapColor;',
        'uniform vec3 uProjectorOverLapFillColor;',
        'uniform float uProjectorOverLapFillOpacity;',
        'uniform vec3 uProjectorOverLapOverlapColor;',
        'uniform float uProjectorOverLapOverlapOpacity;',
        'uniform float uProjectorOverLapOverlapMode;',
        'uniform float uProjectorOverLapOverlapOnly;',
        'uniform float uProjectorOverLapBias;',
        'uniform float uProjectorOverLapFacingThreshold;',
      ];

      if (this.useTextureArray) {
        fragmentHeaderLines.push('uniform sampler2DArray uProjectorOverLapDepthArray;');
      } else {
        fragmentHeaderLines.push(
          ...Array.from({ length: this.maxProjectors }, (_, i) => `uniform sampler2D uProjectorOverLapDepth${i};`)
        );
      }

      const fragmentHeader = fragmentHeaderLines.join('\n');

      let overlapBlocks = '';
      if (this.useTextureArray) {
        overlapBlocks = [
          'float overlapProjectorFillCount = 0.0;',
          'vec3 overlapViewPos = -vViewPosition;',
          'for (int i = 0; i < MAX_PROJECTORS; i++) {',
          '  if (i >= uProjectorOverLapCount) { break; }',
          '  vec4 clip = uProjectorOverLapMatrix[i] * vec4(vProjectorOverLapWorldPosition, 1.0);',
          '  if (clip.w <= 0.0) { continue; }',
          '  vec3 ndc = clip.xyz / clip.w;',
          '  if (ndc.x < -1.0 || ndc.x > 1.0 || ndc.y < -1.0 || ndc.y > 1.0 || ndc.z < -1.0 || ndc.z > 1.0) { continue; }',
          '  vec2 uv = ndc.xy * 0.5 + 0.5;',
          '  float depthSample = unpackRGBAToDepth(texture(uProjectorOverLapDepthArray, vec3(uv, float(i))));',
          '  float fragDepth = ndc.z * 0.5 + 0.5;',
          '  if (fragDepth - uProjectorOverLapBias > depthSample) { continue; }',
          '  if (uProjectorOverLapFacingThreshold > -0.999) {',
          '    vec3 projectorViewPos = (viewMatrix * vec4(uProjectorOverLapPosition[i], 1.0)).xyz;',
          '    vec3 projectorDir = normalize(projectorViewPos - overlapViewPos);',
          '    if (dot(normal, projectorDir) <= uProjectorOverLapFacingThreshold) { continue; }',
          '  }',
          '  overlapProjectorFillCount += 1.0;',
          '}',

          '// Only show overlap areas (2+ projectors)',
          'float projectorOverlap = step(1.5, overlapProjectorFillCount);',
          'vec3 overlapColor = uProjectorOverLapOverlapColor;',
          'float overlapAlpha = projectorOverlap * uProjectorOverLapOverlapOpacity;',
          '',
          'if (uProjectorOverLapOverlapOnly > 0.5) {',
          '  // Overlay pass mode: output overlap color or transparent',
          '  // Scale overlap intensity so additive blending respects alpha.',
          '  outgoingLight = overlapColor * overlapAlpha;',
          '  diffuseColor.a = overlapAlpha;',
          '} else {',
          '  // Normal rendering mode: blend overlap onto surface',
          '  outgoingLight = mix(outgoingLight, overlapColor, overlapAlpha);',
          '  diffuseColor.a = max(diffuseColor.a, overlapAlpha);',
          '}',
        ].join('\n');
      } else {
        overlapBlocks = [
          'float overlapProjectorFillCount = 0.0;',
          ...Array.from({ length: this.maxProjectors }, (_, i) => {
            return [
              `if (uProjectorOverLapCount > ${i}) {`,
              `  vec4 clip${i} = uProjectorOverLapMatrix[${i}] * vec4(vProjectorOverLapWorldPosition, 1.0);`,
              `  if (clip${i}.w > 0.0) {`,
              `    vec3 ndc${i} = clip${i}.xyz / clip${i}.w;`,
              `    if (ndc${i}.x >= -1.0 && ndc${i}.x <= 1.0 && ndc${i}.y >= -1.0 && ndc${i}.y <= 1.0 && ndc${i}.z >= -1.0 && ndc${i}.z <= 1.0) {`,
              `      vec2 uv${i} = ndc${i}.xy * 0.5 + 0.5;`,
              `      float depthSample${i} = texture2D(uProjectorOverLapDepth${i}, uv${i}).x;`,
              `      float fragDepth${i} = ndc${i}.z * 0.5 + 0.5;`,
              `      if (fragDepth${i} - uProjectorOverLapBias <= depthSample${i}) {`,
              `        float facing${i} = dot(normal, normalize((viewMatrix * vec4(uProjectorOverLapPosition[${i}], 1.0)).xyz - overlapViewPos));`,
              `        if (uProjectorOverLapFacingThreshold <= -0.999 || facing${i} > uProjectorOverLapFacingThreshold) {`,
              `          overlapProjectorFillCount += 1.0;`,
              '        }',
              '      }',
              '    }',
              '  }',
              '}',
            ].join('\n');
          }),

          '// Only show overlap areas (2+ projectors)',
          'float projectorOverlap = step(1.5, overlapProjectorFillCount);',
          'vec3 overlapColor = uProjectorOverLapOverlapColor;',
          'float overlapAlpha = projectorOverlap * uProjectorOverLapOverlapOpacity;',
          '',
          'if (uProjectorOverLapOverlapOnly > 0.5) {',
          '  // Overlay pass mode: discard pixels with no overlap',
          '  if (overlapAlpha < 0.001) { discard; }',
          '  // Scale overlap intensity so additive blending respects alpha.',
          '  outgoingLight = overlapColor * overlapAlpha;',
          '  diffuseColor.a = overlapAlpha;',
          '} else {',
          '  // Normal rendering mode: blend overlap onto surface',
          '  outgoingLight = mix(outgoingLight, overlapColor, overlapAlpha);',
          '  diffuseColor.a = max(diffuseColor.a, overlapAlpha);',
          '}',
        ].join('\n');
      }

      const withHeader = shader.fragmentShader.replace('#include <common>', fragmentHeader);
      let fragment = withHeader.replace(
        '#include <output_fragment>',
        [overlapBlocks, '#include <output_fragment>'].join('\n')
      );

      if (fragment === withHeader) {
        fragment = withHeader.replace(
          '#include <opaque_fragment>',
          [overlapBlocks, '#include <opaque_fragment>'].join('\n')
        );
      }

      shader.fragmentShader = fragment;

      userData.projectorFrustumOverlap = {
        applied: true,
        uniforms: shader.uniforms,
      };
    };

    material.needsUpdate = true;
    userData.projectorFrustumOverlap = { applied: true };
    this.materials.add(material);
  }

  private applyUniforms(count: number) {
    for (const material of this.materials) {
      const data = (material.userData as OverlapUserData).projectorFrustumOverlap;
      if (!data?.uniforms) {
        continue;
      }

      data.uniforms.uProjectorOverLapCount.value = count;
      data.uniforms.uProjectorOverLapColor.value = this.color;
      data.uniforms.uProjectorOverLapFillColor.value = this.fillColor;
      data.uniforms.uProjectorOverLapFillOpacity.value = this.fillOpacity;
      data.uniforms.uProjectorOverLapOverlapColor.value = this.overlapColor;
      data.uniforms.uProjectorOverLapOverlapOpacity.value = this.overlapOpacity;
      data.uniforms.uProjectorOverLapOverlapMode.value = this.overlapModeValue;
      data.uniforms.uProjectorOverLapOverlapOnly.value = this.overlapOnly ? 1 : 0;
      data.uniforms.uProjectorOverLapBias.value = this.depthBias;
      data.uniforms.uProjectorOverLapFacingThreshold.value = this.facingThreshold;
      if (this.useTextureArray && data.uniforms.uProjectorOverLapDepthArray) {
        data.uniforms.uProjectorOverLapDepthArray.value = this.depthArrayTarget?.texture ?? null;
      }

      const target = data.uniforms.uProjectorOverLapMatrix.value as THREE.Matrix4[];
      const positionTarget = data.uniforms.uProjectorOverLapPosition.value as THREE.Vector3[];
      for (let i = 0; i < count; i += 1) {
        target[i].copy(this.matrices[i]);
        positionTarget[i].copy(this.projectorPositions[i]);
      }
    }
  }

  dispose() {
    for (const target of this.depthTargets) {
      target.dispose();
    }
    this.depthTargets = [];
    this.depthTextures = [];
    if (this.depthArrayTarget) {
      this.depthArrayTarget.dispose();
    }
    this.depthMaterial.dispose();
    this.overrideMaterial.dispose();
    this.materials.clear();
  }

  private getOverlapModeValue(mode: ProjectorFrustumOverlapMode) {
    switch (mode) {
      case 'none':
        return 0;
      case 'only':
        return 2;
      case 'mix':
      default:
        return 1;
    }
  }
}
